import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, ButtonBase, Stack, Typography, useTheme } from "@mui/material";
import { darken, lighten } from "@mui/material/styles";
import IosShareRounded from "@mui/icons-material/IosShareRounded";
import Brand from "../../../api/Brand";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";
import ColoredIcon from "../../ColoredIcon";
import BackIcon from "../../../img/Arrow_Left.svg";
import ShareableLink from "../../ShareableLink";
import { FONTS } from "../../../constants/styles";
import { FullScreenLoader } from "../../FullScreenLoader";

const NOISE_TEXTURE = `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`;

const Share = () => {
    const theme = useTheme();
    const primary = theme.palette.primary.main;
    const navigate = useNavigate();

    const [address, setAddress] = useState<string | undefined>();
    const [loading, setLoading] = useState(true);

    const [cookies] = useCookies(["uuid", "brand_id"]);
    const { uuid, brand_id } = cookies;

    // Scroll tracking
    const scrollRef = useRef<HTMLDivElement>(null);
    const heroRef = useRef<HTMLDivElement>(null);
    const [scrolledPastHero, setScrolledPastHero] = useState(false);

    const handleScroll = useCallback(() => {
        const el = scrollRef.current;
        const heroEl = heroRef.current;
        if (!el || !heroEl) return;
        setScrolledPastHero(el.scrollTop > heroEl.offsetHeight - 60);
    }, []);

    useEffect(() => {
        if (!uuid || !brand_id) {
            window.location.href = "/";
            return;
        }
        Brand.info(uuid, brand_id)
            .then((res) => {
                setAddress(res?.address);
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [uuid, brand_id]);

    const handleBack = useCallback(() => {
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate("/");
        }
    }, [navigate]);

    const url = address ? `https://${address}.jemloc.com` : "";

    if (loading) return <FullScreenLoader />;

    return (
        <Stack
            ref={scrollRef}
            onScroll={handleScroll}
            flex={1}
            sx={{ overflowY: "auto", overflowX: "hidden", bgcolor: "white" }}
        >
            {/* ── Hero ── */}
            <Stack
                ref={heroRef}
                alignItems="center"
                justifyContent="center"
                sx={{
                    width: "100%",
                    minHeight: 120,
                    position: "relative",
                    zIndex: 0,
                    background: `linear-gradient(180deg, ${lighten(primary, 0.08)} 0%, ${darken(primary, 0.08)} 100%)`,
                    transition: "background 0.3s ease",
                    pt: "40px",
                    pb: 4,
                    "&::after": {
                        content: '""',
                        position: "absolute",
                        inset: 0,
                        opacity: 0.04,
                        backgroundImage: NOISE_TEXTURE,
                        backgroundRepeat: "repeat",
                        backgroundSize: "128px 128px",
                        pointerEvents: "none",
                    },
                }}
            >
                <ButtonBase
                    onClick={handleBack}
                    sx={{
                        position: "absolute", top: 12, left: 10, zIndex: 2,
                        width: 36, height: 36, borderRadius: "50%",
                        bgcolor: "rgba(60,60,60,0.35)",
                        backdropFilter: "blur(16px)",
                        WebkitBackdropFilter: "blur(16px)",
                        border: "1px solid rgba(255,255,255,0.2)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                >
                    <ColoredIcon icon={BackIcon} color="white" size="28px" />
                </ButtonBase>

                <Box
                    sx={{
                        width: 64,
                        height: 64,
                        borderRadius: "50%",
                        bgcolor: "rgba(60,60,60,0.3)",
                        backdropFilter: "blur(16px)",
                        WebkitBackdropFilter: "blur(16px)",
                        border: "1px solid rgba(255,255,255,0.15)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 1,
                    }}
                >
                    <IosShareRounded sx={{ fontSize: 32, color: "white" }} />
                </Box>

                <Box
                    sx={{
                        bgcolor: "rgba(60,60,60,0.35)",
                        backdropFilter: "blur(16px)",
                        WebkitBackdropFilter: "blur(16px)",
                        border: "1px solid rgba(255,255,255,0.2)",
                        borderRadius: "16px",
                        px: 1.5,
                        py: 0.5,
                    }}
                >
                    <Typography fontSize="12px" fontWeight={500} color="white">
                        Share Your App
                    </Typography>
                </Box>
            </Stack>

            {/* ── Sticky Toolbar ── */}
            <Box
                sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 4,
                    bgcolor: "white",
                    borderTopLeftRadius: "24px",
                    borderTopRightRadius: "24px",
                    mt: "-24px",
                    pt: 1.5,
                }}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    sx={{
                        maxHeight: scrolledPastHero ? 52 : 0,
                        opacity: scrolledPastHero ? 1 : 0,
                        overflow: "hidden",
                        transition: "max-height 0.25s ease, opacity 0.2s ease",
                        px: 1,
                        borderBottom: scrolledPastHero
                            ? "1px solid rgba(0,0,0,0.06)"
                            : "none",
                    }}
                >
                    <ButtonBase onClick={handleBack} sx={{ p: 0.5 }}>
                        <ColoredIcon icon={BackIcon} color={primary} size="28px" />
                    </ButtonBase>
                    <Typography
                        fontFamily={FONTS.display}
                        fontSize="16px"
                        fontWeight={600}
                        ml={0.5}
                        noWrap
                        sx={{
                            flex: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                    >
                        Share Your App
                    </Typography>
                </Stack>
            </Box>

            {/* ── Content ── */}
            <Stack
                sx={{
                    position: "relative",
                    zIndex: 1,
                    bgcolor: "white",
                    boxSizing: "border-box",
                    px: 2,
                    pt: 0.5,
                    pb: "calc(4vh + 90px)",
                    "@keyframes fadeSlideIn": {
                        from: { opacity: 0, transform: "translateY(12px)" },
                        to: { opacity: 1, transform: "translateY(0)" },
                    },
                    animation: "fadeSlideIn 0.3s ease-out",
                }}
                spacing={2}
            >
                {/* ── APP LINK section ── */}
                <Typography
                    fontSize="11px"
                    fontWeight={600}
                    color="text.secondary"
                    textTransform="uppercase"
                    letterSpacing="0.5px"
                    mb={-1}
                >
                    App Link
                </Typography>
                {url && (
                    <ShareableLink url={url} label="Your client app" />
                )}
            </Stack>
        </Stack>
    );
};

export default Share;
